import { toneForKey } from './chipColors';

function addDays(date, days) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function ymd(date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
}

function parseISO(value) {
    const [year, month, day] = String(value).split('-').map(Number);
    return new Date(year, month - 1, day);
}

function leaveApproval(leave) {
    return String(leave?.status || 'approved').toLowerCase() === 'pending' ? 'pending' : 'approved';
}

function mergeWindows(windows) {
    const sorted = windows.slice().sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
    const spans = [];
    sorted.forEach((window) => {
        const last = spans[spans.length - 1];
        const lastNext = last ? ymd(addDays(parseISO(last.end), 1)) : '';
        if (last && window.start <= lastNext) {
            if (window.end > last.end) last.end = window.end;
            return;
        }
        spans.push({ start: window.start, end: window.end, boardId: window.boardId });
    });
    return spans;
}

function projectBars(tasks) {
    const groups = new Map();
    tasks.forEach((task) => {
        if (!task.start || !task.end || !task.project) return;
        const key = task.project.id;
        if (!groups.has(key)) groups.set(key, { project: task.project, windows: [] });
        groups.get(key).windows.push({ start: task.start, end: task.end, boardId: task.boardId });
    });
    const bars = [];
    groups.forEach((group) => {
        mergeWindows(group.windows).forEach((span, index) => {
            bars.push({
                id: `project-${group.project.id}-${index}-${span.start}`,
                kind: 'project',
                title: group.project.name,
                meta: group.project.brand || '',
                tone: toneForKey(group.project.id),
                start: span.start,
                end: span.end,
                boardId: span.boardId || '',
                leaveId: null,
            });
        });
    });
    return bars;
}

function userBars(tasks) {
    const groups = new Map();
    tasks.forEach((task) => {
        if (!task.start || !task.end || !task.project) return;
        const people = task.members && task.members.length ? task.members : [null];
        people.forEach((person) => {
            const key = `${person ? person.id : 'none'}|${task.project.id}`;
            if (!groups.has(key)) groups.set(key, { person, project: task.project, windows: [] });
            groups.get(key).windows.push({ start: task.start, end: task.end, boardId: task.boardId });
        });
    });
    const bars = [];
    groups.forEach((group) => {
        mergeWindows(group.windows).forEach((span, index) => {
            const personKey = group.person ? group.person.id : 'none';
            const who = group.person ? group.person.name : 'Unassigned';
            bars.push({
                id: `user-${personKey}-${group.project.id}-${index}-${span.start}`,
                kind: 'user',
                title: who,
                meta: [group.project.name, group.project.brand].filter(Boolean).join(' · '),
                tone: group.person ? toneForKey(group.person.id) : 'gray',
                start: span.start,
                end: span.end,
                boardId: span.boardId || '',
                leaveId: null,
            });
        });
    });
    return bars;
}

function leaveChips(leaves, selectedLeave) {
    return leaves.map((leave) => {
        const approval = leaveApproval(leave);
        const name = leave.person?.name || 'Leave';
        return {
            id: `leave-${leave.id}`,
            kind: 'leave',
            title: `${name} · ${leave.type || 'Leave'}`,
            meta: approval === 'pending' ? 'Pending leave' : 'Approved leave',
            tone: approval === 'pending' ? 'blue' : 'green',
            start: leave.start,
            end: leave.end,
            boardId: '',
            leaveId: leave.id,
            selected: selectedLeave === leave.id,
        };
    });
}

function eventsOnDay(dayYmd, bars) {
    const events = bars.filter((bar) => bar.start && bar.end && dayYmd >= bar.start && dayYmd <= bar.end);
    events.sort((a, b) => {
        if (a.kind !== b.kind) return a.kind === 'leave' ? -1 : 1;
        return a.title.localeCompare(b.title);
    });
    return events;
}

export function buildEventsByDay(weeks, tasks, leaves, colorBy, selectedLeave) {
    const bars = colorBy === 'assignee' ? userBars(tasks) : projectBars(tasks);
    const shown = colorBy === 'project' ? bars : bars.concat(leaveChips(leaves, selectedLeave));
    const map = new Map();
    weeks.forEach((weekStart) => {
        for (let i = 0; i < 7; i += 1) {
            const key = ymd(addDays(weekStart, i));
            map.set(key, eventsOnDay(key, shown));
        }
    });
    return map;
}
